import { NotImplementedError, ObjType, publisherAllosListToService, serviceToPublisherAllowList, Validation, serviceToPublisherAllowListPubs } from "../../../types";
import { ObservableHandler } from "../../../handlers/observable";
import { PublisherAllowListSaveFailed, PublisherAllowListFetchFailed } from "../errors";
import { parseIdFilter } from "../../../handlers/filter";
import { isServicePage } from "../../../handlers/page";
import axios from "axios";
import { Observable } from "rxjs";
import { filtersHasEmptyIdParam } from "./filters";
import { validators } from "./validators";
import Uploads from "./uploads";
import Templates from "./templates";
import { toQueryParams } from "./filters/params";
import { toFilterGroupObjects } from "./filters/objects";
class PublisherAllowList extends ObservableHandler {
    constructor(sdk) {
        super(sdk, "publishers-allow-list", { atomize: true });
        this.toPublisherAllowList = (allowList) => {
            return serviceToPublisherAllowList(allowList);
        };
        this.url = `${this.sdk.urls.baseAPIUrl}/pub_group`;
        this.validators = validators();
        this.templates = new Templates(this.sdk);
        this.uploads = new Uploads(this.sdk);
    }
    /**
     * @param filters: filters to determine what publisher allow list to get.
     * @return: an observable yielding the requested publisher allow list.
     */
    findItems(filters, sort) {
        return new Observable((subscriber) => {
            const idFilter = parseIdFilter(filters);
            if (filtersHasEmptyIdParam(filters.where)) {
                subscriber.next([]);
                subscriber.complete();
            }
            else {
                const promise = idFilter.size === 1
                    ? this.getPublisherAllowList(idFilter.values().next().value).then((list) => [list])
                    : this.getPublisherAllowLists(filters, sort);
                promise
                    .then((lists) => {
                    subscriber.next(lists);
                    subscriber.complete();
                })
                    .catch((error) => {
                    subscriber.error(error);
                });
            }
        });
    }
    async getPublisherAllowLists(filters = {}, sort) {
        const params = toQueryParams(filters, sort);
        const pageSize = filters?.paging?.size || 10;
        return this.cache.promise(`${this.url}${params}`, () => axios
            .get(`${this.url}${params}`, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => {
            const allowLists = res.data?.map(this.toPublisherAllowList) || [];
            let results = allowLists;
            if (isServicePage(res)) {
                results = {
                    page: {
                        count: res.paging_info?.count,
                        token: res.paging_info?.token,
                        size: pageSize
                    },
                    data: allowLists
                };
            }
            return results;
        })
            .catch((error) => {
            throw new PublisherAllowListFetchFailed(error);
        }));
    }
    async getPublisherAllowList(id) {
        return this.cache.promise(`${this.url}/${id}`, () => axios
            .get(`${this.url}/${id}`, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => this.toPublisherAllowList(res?.data))
            .catch((error) => {
            throw new PublisherAllowListFetchFailed(error);
        }));
    }
    saveItem(data, options) {
        const { skipValidation } = options || {};
        const promise = data.id ? this.update(data, skipValidation) : this.create(data, skipValidation);
        return new Observable((observer) => {
            promise
                .then(({ data: res }) => {
                const allowList = this.toPublisherAllowList(Array.isArray(res.data) ? res.data[0] : res.data);
                this.cache.reconcile("save", allowList);
                observer.next(allowList);
                observer.complete();
            })
                .catch((error) => {
                if (error instanceof Validation) {
                    observer.error(new Error(error.values().next().value));
                    return;
                }
                observer.error(new PublisherAllowListSaveFailed(error));
            });
        });
    }
    async create(allowListBase, skipValidation = false) {
        const allowLists = (await this.make(allowListBase));
        if (!skipValidation) {
            const errors = await this.validate(allowLists);
            if (errors.size) {
                throw errors;
            }
        }
        return axios.post(`${this.url}`, { data: [this.toService(allowLists)] }, {
            headers: {
                "Content-Type": "application/json"
            }
        });
    }
    async update(allowList, skipValidation = false) {
        if (!skipValidation) {
            const errors = await this.validate(allowList);
            if (errors.size) {
                throw errors;
            }
        }
        return axios.patch(this.url, { data: [this.toService(allowList)] }, {
            headers: {
                "Content-Type": "application/json"
            }
        });
    }
    async upload(file) {
        const listOfPublishers = (await this.uploads.save(file)) || [];
        this.cache.reconcile("save", listOfPublishers);
        return serviceToPublisherAllowListPubs(listOfPublishers) || [];
    }
    parseFilter(filters) {
        return toFilterGroupObjects(filters || new Map());
    }
    make(defaults) {
        return new Promise((resolve, reject) => {
            this.sdk.cryptography
                .mintKey(ObjType.PUBGROUP)
                .then((id) => {
                const allowList = {
                    id,
                    name: "",
                    parentId: "",
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    createdBy: undefined,
                    updatedBy: undefined,
                    pubs: [],
                    mediaType: undefined,
                    status: undefined,
                    ...defaults
                };
                resolve(allowList);
            })
                .catch((error) => {
                reject(error);
            });
        });
    }
    async deleteItem() {
        return new Promise((_, reject) => {
            reject(new NotImplementedError("delete"));
        });
    }
    toService(allowList) {
        return publisherAllosListToService(allowList);
    }
}
export default PublisherAllowList;
//# sourceMappingURL=index.js.map